import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import TaskStageBadge from "../components/TaskStageBadge";
import { ListTodo, Link2, ExternalLink, CalendarDays } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * "My Tasks" — the work assigned to ME as the executor.
 *
 * `GET /tasks` narrows to the caller only for role=artist, so a staff member
 * who takes tasks alongside another job (an art director, say) had no list of
 * their own work and no way to reach it — which is how it was reported:
 * "I need a My Tasks button like other executors". backlog 833d248d.
 */

const fmt = (d) => {
  if (!d) return null;
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(d).slice(0, 10);
};

const daysTo = (d) => {
  const m = String(d || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return Math.round((new Date(+m[1], +m[2] - 1, +m[3]) - t) / 86400000);
};

const MyTasksPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");

  useEffect(() => {
    api.get("/me/assigned-tasks")
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], open_count: 0, total: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  const items = data?.items || [];
  const open = items.filter((t) => !t.is_done);
  const done = items.filter((t) => t.is_done);
  const shown = tab === "open" ? open : done;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
          <ListTodo className="h-6 w-6 text-orange-500" />
          My Tasks
        </h1>
        <p className="text-sm text-zinc-500">Work assigned to you as the executor</p>
      </div>

      <div className="flex gap-1 rounded-xl border border-zinc-800 bg-zinc-900/60 p-1 w-fit">
        {[["open", "Open", open.length], ["done", "Done", done.length]].map(([k, label, n]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors",
              tab === k ? "bg-zinc-800 text-zinc-50 font-semibold" : "text-zinc-400 hover:text-zinc-100")}>
            {label}<span className="text-[11px] text-zinc-500">{n}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center text-zinc-500">
            {tab === "open" ? "No open tasks" : "No finished tasks"}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-0 divide-y divide-zinc-800/60">
            {shown.map((t) => {
              const left = daysTo(t.deadline);
              const late = left != null && left < 0 && !t.is_done;
              // Closed work with nothing attached is the gap people kept hitting:
              // the task is done and the deliverable link lives in a chat.
              const missingResult = t.is_done && t.versions_count === 0;
              return (
                <button key={t.id} type="button" onClick={() => navigate(`/tasks/${t.id}`)}
                  className="w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-4 py-3 text-left hover:bg-zinc-800/40 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm text-zinc-100 truncate" title={t.name}>{t.name}</p>
                    <p className="text-xs text-zinc-500 truncate">
                      {t.project_name}
                      {t.task_type ? <span className="text-zinc-600"> · {t.task_type}</span> : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 whitespace-nowrap">
                    {t.deadline && (
                      <span className={cn("inline-flex items-center gap-1 text-xs tabular-nums",
                        late ? "text-red-500" : "text-zinc-400")}>
                        <CalendarDays className="h-3.5 w-3.5" />
                        {fmt(t.deadline)}
                      </span>
                    )}
                    {t.versions_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-zinc-400">
                        <Link2 className="h-3.5 w-3.5" />{t.versions_count}
                      </span>
                    ) : missingResult ? (
                      <span className="text-xs text-amber-400">no result attached</span>
                    ) : null}
                    <TaskStageBadge task={t} size="sm" />
                    <ExternalLink className="h-3.5 w-3.5 text-zinc-600" />
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MyTasksPage;
